import { useState, useEffect } from "react";
import {
  addDoc,
  collection,
  getDocs,
  Timestamp,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import Modal from "./Modal";
import Select from "react-select";
import Swal from "sweetalert2";
import { FaTrash, FaEdit, FaThumbtack } from "react-icons/fa";
import DOMPurify from 'dompurify';
import "./messages.css";

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [showInFooter, setShowInFooter] = useState(false);
  const [editMessageId, setEditMessageId] = useState(null);
  const [toastType, setToastType] = useState("info");
  const [ouSelections, setOuSelections] = useState({});

  const getMessagesFromFirestore = async () => {
    try {
      const messagesRef = collection(db, "messages");
      const snapshot = await getDocs(messagesRef);

      const messagesList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Eerst pinned berichten, daarna op createdOn (desc)
      const sorted = messagesList.sort((a, b) => {
        const aPinned = a.order === "pinned" ? 0 : 1;
        const bPinned = b.order === "pinned" ? 0 : 1;

        if (aPinned !== bPinned) return aPinned - bPinned;
        return b.createdOn?.seconds - a.createdOn?.seconds;
      });

      return sorted;
    } catch (error) {
      console.error("Error fetching messages:", error);
      return [];
    }
  };

  const addMessageToFirestore = async (
    messageContent,
    showInFooter,
    toastType
  ) => {
    try {
      const messagesRef = collection(db, "messages");
      await addDoc(messagesRef, {
        message: messageContent,
        createdOn: Timestamp.fromDate(new Date()),
        showInFooter: showInFooter,
        toastType: toastType,
        visibleForOU: null,
        order: null,
      });
      console.log("Message successfully added!");
    } catch (error) {
      console.error("Error adding message:", error);
    }
  };

  const toggleShowInFooter = async (messageId, currentValue, ouValue = "") => {
    try {
      if (!currentValue) {
        const result = await Swal.fire({
          title: "Bericht tonen op tegelpagina?",
          text: "Wil je dit bericht tonen op de tegelpagina?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Ja, tonen",
          cancelButtonText: "Annuleren",
          customClass: {
            popup: "small-swal",
            confirmButton: "btn-confirm",
            cancelButton: "btn-cancel",
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return; // ⛔️ gebruiker heeft geannuleerd
        }
      }

      const messageRef = doc(db, "messages", messageId);
      const updates = {
        showInFooter: !currentValue,
      };

      if (!currentValue) {
        updates.visibleForOU = ouValue || null;
      }

      await updateDoc(messageRef, updates);
      const fetchedMessages = await getMessagesFromFirestore();
      setMessages(fetchedMessages);

      const updatedSelections = {};
      fetchedMessages.forEach((msg) => {
        updatedSelections[msg.id] = msg.visibleForOU || "";
      });
      setOuSelections(updatedSelections);
    } catch (error) {
      console.error("Error updating showInFooter/OU:", error);
    }
  };

  const deleteMessageFromFirestore = async (messageId) => {
    try {
      const result = await Swal.fire({
        title: "Weet je het zeker?",
        text: "Dit bericht wordt permanent verwijderd.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Verwijderen",
        cancelButtonText: "Annuleren",
        customClass: {
          popup: "small-swal",
          confirmButton: "btn-confirm",
          cancelButton: "btn-cancel",
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        const messageRef = doc(db, "messages", messageId);
        await deleteDoc(messageRef);
        const fetchedMessages = await getMessagesFromFirestore();
        setMessages(fetchedMessages);

        // ✅ Succesmelding gestyled
        Swal.fire({
          title: "Verwijderd!",
          text: "Het bericht is verwijderd.",
          icon: "success",
          confirmButtonText: "Oke",
          customClass: {
            popup: "small-swal",
            confirmButton: "btn-confirm",
          },
          buttonsStyling: false,
        });
      } else {
        // ✅ Annuleermelding gestyled
        Swal.fire({
          title: "Geannuleerd",
          text: "Het bericht is niet verwijderd.",
          icon: "info",
          confirmButtonText: "Oke",
          customClass: {
            popup: "small-swal",
            confirmButton: "btn-cancel",
          },
          buttonsStyling: false,
        });
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const checkUserAuth = () => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    });
  };

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      const fetchedMessages = await getMessagesFromFirestore();
      setMessages(fetchedMessages);

      // Initialiseer OU-selecties per bericht
      const initialOU = {};
      fetchedMessages.forEach((msg) => {
        initialOU[msg.id] = msg.visibleForOU || "";
      });
      setOuSelections(initialOU);

      setLoading(false);
    };

    fetchMessages();
    checkUserAuth();
  }, []);

  const handleNewMessage = async () => {
    if (user) {
      if (editMessageId) {
        // update message tekst op basis van newMessage
        const messageRef = doc(db, "messages", editMessageId);
        await updateDoc(messageRef, {
          message: newMessage,
        });
        setEditMessageId(null);
      } else {
        await addMessageToFirestore(newMessage, showInFooter, toastType);
      }

      const fetchedMessages = await getMessagesFromFirestore();
      setMessages(fetchedMessages);
      setNewMessage("");
      setIsModalOpen(false);
    } else {
      alert("You must be logged in to post a message.");
    }
  };

  // Handle toast type change
  const handleToastTypeChange = async (event, messageId) => {
    const selectedToastType = event.target.value;
    setToastType(selectedToastType);

    // Update toastType in Firestore for the message
    const messageRef = doc(db, "messages", messageId);
    await updateDoc(messageRef, { toastType: selectedToastType });

    // Fetch the updated messages again to re-render with the correct toastType
    const fetchedMessages = await getMessagesFromFirestore();
    setMessages(fetchedMessages);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp.seconds * 1000);
    const formattedDate = date.toLocaleDateString("nl-NL", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${formattedDate} om ${formattedTime}`;
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      fontSize: "0.85rem",
      border: "none",
      borderBottomLeftRadius: "6px",
      borderBottomRightRadius: "6px",
      boxShadow: "none", // 🔹 Geen focus shadow
      outline: "none", // 🔹 Geen browser-outline
      "&:hover": {
        border: "none", // 🔹 Ook geen border op hover
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: "#000", // zwarte tekst van de geselecteerde optie
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.85rem",
      color: "#000", // zwarte tekst in de dropdown lijst
      backgroundColor: state.isFocused ? "#eef5fc" : "white",
    }),
  };

  const toastOptions = [
    { value: "info", label: "Informatie" },
    { value: "success", label: "Success" },
    { value: "error", label: "Fout" },
    { value: "warn", label: "Waarschuwing" },
  ];

  const ouOptions = [
    { value: "", label: "Zuidzorg" },
    { value: "/Wijkzorg", label: "Wijkzorg" },
    { value: "/TC", label: "Thuiscoaching" },
    { value: "/Vastgoed", label: "Vastgoed" },
    { value: "/ZSC", label: "Stichting" },
  ];

  return (
    <>
      <div className="messages-container">
        <div className="message-header">
          {user && (
            <button
              className="newmsgbutton"
              onClick={() => {
                setNewMessage(""); // 🧹 reset inhoud
                setEditMessageId(null); // 🧹 reset edit-mode
                setIsModalOpen(true); // 🔓 open modal
              }}
            >
              Nieuw Bericht
            </button>
          )}
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleNewMessage}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          setShowInFooter={setShowInFooter}
          editMessageId={editMessageId}
        />

        <div className="grid-container grid-container--fill">
          {loading ? (
            <ul className="message-list">
              <li className="message-card">
                <div className="message-content">Berichten laden...</div>
              </li>
            </ul>
          ) : (
            <div className="message-wrapper">
              <ul className="message-list">
                {messages.length > 0 ? (
                  messages.map((msg, index) => (
                    <li key={index} className={`message-card ${msg.toastType}`}>
                      {user && (
                        <button
                          className={`pin-button ${msg.order === "pinned" ? "pinned" : ""
                            }`}
                          title={
                            msg.order === "pinned" ? "Unpin" : "Pin bovenaan"
                          }
                          onClick={async () => {
                            const newOrder =
                              msg.order === "pinned" ? null : "pinned";
                            const messageRef = doc(db, "messages", msg.id);
                            await updateDoc(messageRef, { order: newOrder });

                            const updatedMessages =
                              await getMessagesFromFirestore();
                            setMessages(updatedMessages);
                          }}
                        >
                          <FaThumbtack />
                        </button>
                      )}
                      <span className="message-date">
                        {formatDate(msg.createdOn)}
                      </span>

                      {user && (
                        <>
                          <div className="message-action-labels">
                            <span>Type</span>
                            <span>Doelgroep</span>
                            <span>Zichtbaarheid</span>
                            <span>Bewerken</span>
                            <span>Verwijderen</span>
                          </div>

                          <div className="message-actions">
                            {/* Type */}
                            <Select
                              styles={customStyles}
                              options={toastOptions}
                              value={toastOptions.find(
                                (opt) => opt.value === msg.toastType
                              )}
                              onChange={(selectedOption) =>
                                handleToastTypeChange(
                                  { target: { value: selectedOption.value } },
                                  msg.id
                                )
                              }
                            />

                            {/* OU selectie */}
                            <Select
                              styles={customStyles}
                              options={ouOptions}
                              value={ouOptions.find(
                                (opt) => opt.value === ouSelections[msg.id]
                              )}
                              onChange={async (selectedOption) => {
                                const newValue = selectedOption.value;

                                setOuSelections((prev) => ({
                                  ...prev,
                                  [msg.id]: newValue,
                                }));

                                try {
                                  const messageRef = doc(
                                    db,
                                    "messages",
                                    msg.id
                                  );
                                  await updateDoc(messageRef, {
                                    visibleForOU: newValue || null,
                                  });

                                  const updatedMessages =
                                    await getMessagesFromFirestore();
                                  setMessages(updatedMessages);
                                } catch (error) {
                                  console.error(
                                    "Error updating visibleForOU:",
                                    error
                                  );
                                }
                              }}
                            />

                            {/* Toggle button */}
                            <button
                              onClick={() =>
                                toggleShowInFooter(
                                  msg.id,
                                  msg.showInFooter,
                                  ouSelections[msg.id] || ""
                                )
                              }
                              className={`toggle-footer-button ${msg.showInFooter
                                  ? "message-off-button"
                                  : "message-on-button"
                                }`}
                            >
                              {msg.showInFooter ? "Uitzetten" : "Aanzetten"}
                            </button>
                            {/* Edit */}
                            <button
                              onClick={() => {
                                setEditMessageId(msg.id);
                                setNewMessage(msg.message); // ✅ zet de tekst voor in de modal
                                setToastType(msg.toastType);
                                setIsModalOpen(true);
                              }}
                              className="edit-button"
                            >
                              <FaEdit />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => deleteMessageFromFirestore(msg.id)}
                              className="delete-button"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </>
                      )}
                      <div
                        className="message-content"
                        dangerouslySetInnerHTML={{ __html: msg.message }}
                      />
                    </li>
                  ))
                ) : (
                  <li className="message-card">
                    <div className="message-content">
                      Er zijn geen berichten.
                    </div>
                  </li>
                )}
              </ul>
              <div className="message-spacer" />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
